// Client-side auth + encryption for a no-backend wallet PWA (CLAUDE.md /
// feature-harden.md ASVS-3, ASVS-6/8).
//
// One random AES-256-GCM "vault key" (VK) per profile is the only thing
// that ever encrypts data; VK itself is never persisted raw. It's wrapped
// (AES-GCM key-wrap) up to twice:
//   - under a PIN-derived key (PBKDF2-SHA256, 600k iterations + a random
//     per-profile salt) — ALWAYS present. This is the recovery path if the
//     device's platform authenticator is ever lost, reset, or unavailable,
//     so it is never optional, even for a profile that also has biometric
//     unlock enrolled.
//   - under a WebAuthn PRF-derived key, when the platform authenticator
//     supports the "prf" extension — a convenience unlock layered on top
//     of the PIN wrap, never a replacement for it.
// Unlocking means recovering VK via either wrap; both yield the identical
// key, so anything encrypted under it (see actions.js's saved-recipients
// vault) is reachable from whichever path the user unlocks with.
//
// On PBKDF2's role: 600k iterations is the defense for the ~20-bit-entropy
// 6-digit PIN against offline guessing. It is cryptographically inert on
// the PRF path — that secret is already 256 bits, far beyond brute-force
// range — so running it through the same PBKDF2 step there is just code
// reuse, not a security mechanism. On that path the actual security comes
// from the authenticator's own user-verification (biometric/PIN) gate.
// Documented here so this isn't mistaken for PBKDF2 protecting biometric
// unlock the same way it protects the PIN.

const PBKDF2_ITERATIONS = 600000;
const PBKDF2_HASH = 'SHA-256';

// The WebAuthn PRF extension's "eval.first" salt and the fixed PBKDF2 salt
// used to turn that PRF secret into a KEK are both constants, not random —
// unlike the PIN salt, there's no rainbow-table risk to defend against
// here (the PRF output is already unique, high-entropy per credential), so
// randomizing them would add storage with no security benefit.
const PRF_EVAL_SALT = new TextEncoder().encode('tynti-vault-prf-eval-v1');
const PRF_KEK_SALT = 'dHludGktdmF1bHQta2VrLXYx'; // base64("tynti-vault-kek-v1")

// WebAuthn's own `timeout` option bounds how long the browser waits on its
// native prompt, but isn't reliably honored in every environment (a
// platform reporting itself "available" with no real authenticator behind
// it — automated test runners, some virtualized/CI browsers — can leave
// create()/get() hanging well past it). Race it against this as a hard
// backstop so a stuck prompt can never block onboarding or unlock
// indefinitely; callers already treat a null/rejected result as "fall
// back to PIN", so timing out here is just another way to reach that.
const WEBAUTHN_TIMEOUT_MS = 30000;
function withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('WebAuthn timed out')), WEBAUTHN_TIMEOUT_MS))
  ]);
}

function toB64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function fromB64(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}

function generateSalt() {
  return toB64(crypto.getRandomValues(new Uint8Array(16)));
}

// Shared PBKDF2 derivation for both the PIN and PRF-secret inputs — see the
// PBKDF2 role note above for why it's load-bearing only on the PIN path.
async function deriveKek(secretBytes, saltB64) {
  const keyMaterial = await crypto.subtle.importKey('raw', secretBytes, 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: fromB64(saltB64), iterations: PBKDF2_ITERATIONS, hash: PBKDF2_HASH },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey']
  );
}

export async function generateVaultKey() {
  // extractable: true only so wrapKey below can serialize it — the wrapped
  // form is the only thing ever persisted. The live CryptoKey held in
  // memory during a session comes from unwrapKey with extractable:false
  // (see unwrapVaultKey), so raw key bytes are never exposed to JS there.
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

async function wrapVaultKey(vaultKey, kek) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.wrapKey('raw', vaultKey, kek, { name: 'AES-GCM', iv });
  return { wrapped: toB64(wrapped), iv: toB64(iv) };
}
async function unwrapVaultKey(wrappedB64, ivB64, kek) {
  return crypto.subtle.unwrapKey(
    'raw', fromB64(wrappedB64), kek, { name: 'AES-GCM', iv: fromB64(ivB64) },
    { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
  );
}

/* ---- PIN wrap (always present) ----------------------------------------- */
export async function createPinWrap(pin, vaultKey) {
  const salt = generateSalt();
  const kek = await deriveKek(new TextEncoder().encode(pin), salt);
  const { wrapped, iv } = await wrapVaultKey(vaultKey, kek);
  return { wrapped, iv, salt };
}
// Throws (AES-GCM auth-tag failure) on a wrong PIN — callers must try/catch;
// that failure IS the passcode check, so there's no separate verifier to
// keep in sync with it.
export async function unwrapWithPin(pin, pinWrap) {
  const kek = await deriveKek(new TextEncoder().encode(pin), pinWrap.salt);
  return unwrapVaultKey(pinWrap.wrapped, pinWrap.iv, kek);
}

/* ---- WebAuthn PRF wrap (optional convenience layer) -------------------- */
export function webauthnSupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}
export async function platformAuthenticatorAvailable() {
  if (!webauthnSupported()) return false;
  try { return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { return false; }
}

async function evalPrf(credentialIdB64) {
  let assertion;
  try {
    assertion = await withTimeout(navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: credentialIdB64 ? [{ id: fromB64(credentialIdB64), type: 'public-key' }] : [],
        userVerification: 'required',
        timeout: WEBAUTHN_TIMEOUT_MS,
        extensions: { prf: { eval: { first: PRF_EVAL_SALT } } }
      }
    }));
  } catch {
    return null; // cancelled, timed out, no matching credential, or blocked
  }
  const ext = assertion.getClientExtensionResults();
  const first = ext && ext.prf && ext.prf.results && ext.prf.results.first;
  return first ? new Uint8Array(first) : null;
}

// Registers a new platform passkey and, only if the authenticator actually
// supports the "prf" extension, wraps `vaultKey` under a key derived from
// its PRF output. Returns null — not an error — if WebAuthn or PRF isn't
// available, the user declines/cancels the platform prompt, or the PRF
// output isn't reproducible; PIN-only is always a fully valid, fully
// secure setup on its own, so any of these is a silent no-op fallback,
// never a failure the caller needs to surface.
export async function registerWebauthn(profileName, vaultKey) {
  if (!(await platformAuthenticatorAvailable())) return null;

  let credential;
  try {
    credential = await withTimeout(navigator.credentials.create({
      publicKey: {
        rp: { name: 'Tynti' },
        user: {
          id: crypto.getRandomValues(new Uint8Array(16)),
          name: profileName || 'Tynti account',
          displayName: profileName || 'Tynti account'
        },
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
        authenticatorSelection: { userVerification: 'required', residentKey: 'preferred' },
        timeout: WEBAUTHN_TIMEOUT_MS,
        extensions: { prf: { eval: { first: PRF_EVAL_SALT } } }
      }
    }));
  } catch {
    return null;
  }

  const credentialId = toB64(credential.rawId);
  const createExt = credential.getClientExtensionResults();
  const fromCreate = createExt && createExt.prf && createExt.prf.results && createExt.prf.results.first;
  let secret = fromCreate ? new Uint8Array(fromCreate) : null;
  // Some authenticators only evaluate "prf" on assertion (get()), not at
  // creation — a follow-up get() during the same registration finds out.
  if (!secret) secret = await evalPrf(credentialId);
  if (!secret) return null; // this authenticator doesn't support prf at all

  // Confirm determinism before committing to it: the same (credential,
  // salt) pair must always yield the same secret, or a wrap made from it
  // now could never be unwrapped again on a real future unlock.
  const confirm = await evalPrf(credentialId);
  if (!confirm || toB64(confirm) !== toB64(secret)) return null;

  const kek = await deriveKek(secret, PRF_KEK_SALT);
  const { wrapped, iv } = await wrapVaultKey(vaultKey, kek);
  return { credentialId, wrapped, iv };
}

// Returns null (not a thrown error) whenever biometric unlock can't
// complete for any reason — cancelled prompt, no matching authenticator,
// PRF unavailable this time — so callers fall back to the PIN UI rather
// than surface a raw exception.
export async function unwrapWithWebauthn(webauthnWrap) {
  const secret = await evalPrf(webauthnWrap.credentialId);
  if (!secret) return null;
  const kek = await deriveKek(secret, PRF_KEK_SALT);
  try {
    return await unwrapVaultKey(webauthnWrap.wrapped, webauthnWrap.iv, kek);
  } catch {
    return null;
  }
}

/* ---- Encrypting arbitrary local data under the vault key --------------- */
export async function encryptJSON(vaultKey, value) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, vaultKey, data);
  return { iv: toB64(iv), data: toB64(ciphertext) };
}
export async function decryptJSON(vaultKey, envelope) {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromB64(envelope.iv) }, vaultKey, fromB64(envelope.data)
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
}

/* ---- In-memory session key (never persisted, cleared on lock) --------- */
let sessionVaultKey = null;
let sessionProfileId = null;
export function setSessionVault(profileId, vaultKey) {
  sessionProfileId = profileId;
  sessionVaultKey = vaultKey;
}
export function getSessionVault() {
  return { profileId: sessionProfileId, vaultKey: sessionVaultKey };
}
export function clearSessionVault() {
  sessionProfileId = null;
  sessionVaultKey = null;
}
